import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { createApiClient } from "../api/client";
import {
  Search, SlidersHorizontal, Download, Database,
  ChevronLeft, ChevronRight, AlertCircle, Loader2,
  X, Calendar, Activity, Phone, DollarSign, Check
} from "lucide-react";

export default function MyLeads() {
  const { token, logout } = useAuth();
  const apiClient = useMemo(() => createApiClient(token, logout), [token, logout]);

  // --- Global Metadata State ---
  const [buyers, setBuyers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  // --- Filter State ---
  const [stagedFilters, setStagedFilters] = useState(() => {
    const saved = localStorage.getItem("myLeadsFilters");
    return saved ? JSON.parse(saved) : {
      buyerId: "",
      supplierCode: "",
      fromDate: "",
      toDate: "",
      minPrice: "",
      maxPrice: "",
      search: "",
      phone: "",
      status: "",
      responseSearch: ""
    };
  });

  const [appliedFilters, setAppliedFilters] = useState(stagedFilters);

  // --- Data & UI State ---
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [hasSearched, setHasSearched] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("myLeadsFilters", JSON.stringify(stagedFilters));
  }, [stagedFilters]);

  // Initial Load: Buyers
  useEffect(() => {
    const fetchBuyers = async () => {
      try {
        const response = await apiClient(`/Leads/buyers`);
        if (!response.ok) throw new Error("Failed to fetch buyers.");
        setBuyers(await response.json());
      } catch (err) {
        setError("Unable to load active buyers.");
      }
    };
    fetchBuyers();
  }, [apiClient]);

  // Dependent Fetch: Suppliers
  useEffect(() => {
    if (!stagedFilters.buyerId) {
      setSuppliers([]);
      return;
    }
    const fetchSuppliers = async () => {
      try {
        const response = await apiClient(`/Leads/suppliers/${stagedFilters.buyerId}`);
        if (!response.ok) throw new Error("Failed to fetch suppliers.");
        setSuppliers(await response.json());
      } catch (err) {
        console.error(err);
      }
    };
    fetchSuppliers();
  }, [stagedFilters.buyerId, apiClient]);

  const handleSearch = useCallback(async (page = 1) => {
    if (!stagedFilters.buyerId || !stagedFilters.supplierCode) {
      setError("Please select both a Website and a Supplier.");
      return;
    }

    setIsLoading(true);
    setIsSuccess(false);
    setError("");
    setHasSearched(true);
    setCurrentPage(page);
    setAppliedFilters(stagedFilters);

    // UX Refine: Auto-collapse filters on search
    setIsFiltersOpen(false);

    try {
      const params = new URLSearchParams({
        buyerId: stagedFilters.buyerId,
        supplierCode: stagedFilters.supplierCode,
        page: page,
        pageSize: pageSize,
        ...(stagedFilters.fromDate && { fromDate: stagedFilters.fromDate }),
        ...(stagedFilters.toDate && { toDate: stagedFilters.toDate }),
        ...(stagedFilters.minPrice && { minPrice: stagedFilters.minPrice }),
        ...(stagedFilters.maxPrice && { maxPrice: stagedFilters.maxPrice }),
        ...(stagedFilters.search && { search: stagedFilters.search }),
        ...(stagedFilters.phone && { phone: stagedFilters.phone }),
        ...(stagedFilters.status && { status: stagedFilters.status }),
        ...(stagedFilters.responseSearch && { responseSearch: stagedFilters.responseSearch }),
      });

      const response = await apiClient(`/Leads/myleads?${params.toString()}`);
      if (!response.ok) throw new Error("Server error occurred.");
      const data = await response.json();
      setLeads(data);
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 1500);
    } catch (err) {
      setError(err.message || "Failed to fetch leads.");
    } finally {
      setIsLoading(false);
    }
  }, [stagedFilters, pageSize, apiClient]);

  // --- DYNAMIC HEADER & HIGHLIGHT LOGIC ---
  const activeSearchKeys = useMemo(() => {
    return Object.entries(appliedFilters)
      .filter(([k, v]) => !["buyerId", "supplierCode", "responseSearch"].includes(k) && v !== "")
      .map(([k]) => k);
  }, [appliedFilters]);

  // Intelligent mapping of filter keys to probable database column names
  const isHeaderActive = useCallback((header) => {
    const lowerHeader = header.toLowerCase();
    return activeSearchKeys.some(key => {
      if (key === 'search') return ['first', 'last', 'email', 'name'].some(match => lowerHeader.includes(match));
      if (key === 'phone') return lowerHeader.includes('phone');
      if (key === 'status') return lowerHeader.includes('status');
      if (key.includes('Date')) return lowerHeader.includes('date');
      if (key.includes('Price')) return ['price', 'cost', 'revenue', 'amount'].some(match => lowerHeader.includes(match));
      return lowerHeader.includes(key.toLowerCase());
    });
  }, [activeSearchKeys]);

  const tableHeaders = useMemo(() => {
    if (leads.length === 0) return [];
    const allKeys = Object.keys(leads[0]);

    // Move searched columns to the front
    const sorted = [...allKeys].sort((a, b) => {
      const aActive = isHeaderActive(a);
      const bActive = isHeaderActive(b);
      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;
      return 0;
    });

    return sorted;
  }, [leads, isHeaderActive]);

  const handleBuyerChange = (e) => {
    const val = e.target.value;
    setStagedFilters(prev => ({ ...prev, buyerId: val, supplierCode: "" }));
    setLeads([]);
    setHasSearched(false);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setStagedFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = (e) => {
    if (e) e.preventDefault();
    const reset = {
      buyerId: stagedFilters.buyerId,
      supplierCode: stagedFilters.supplierCode,
      fromDate: "",
      toDate: "",
      minPrice: "",
      maxPrice: "",
      search: "",
      phone: "",
      status: "",
      responseSearch: ""
    };
    setStagedFilters(reset);
    setAppliedFilters(reset);
  };

  const removeFilter = (key) => {
    setStagedFilters(prev => ({ ...prev, [key]: "" }));
    setAppliedFilters(prev => ({ ...prev, [key]: "" }));
  };

  const activeFilterChips = useMemo(() => {
    const exclude = ["buyerId", "supplierCode", "responseSearch"];
    return Object.entries(appliedFilters)
      .filter(([key, value]) => !exclude.includes(key) && value !== "")
      .map(([key, value]) => {
        let label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        if (key === 'search') label = 'Keyword';
        return { key, label, value };
      });
  }, [appliedFilters]);

  const handleExportCSV = () => {
    if (leads.length === 0) return;
    const headers = tableHeaders.join(",");
    const csvRows = [headers];
    leads.forEach((lead) => {
      const row = tableHeaders.map(header => {
        const val = lead[header] === null || lead[header] === undefined ? "" : lead[header];
        return `"${String(val).replace(/"/g, '""')}"`;
      });
      csvRows.push(row.join(","));
    });
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `leads_export_${new Date().getTime()}.csv`;
    link.click();
  };

  const tableVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } }
  };

  return (
    <div className="max-w-[1200px] mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 text-[14px]">
      <div className="mb-6">
        <h1 className="text-[24px] font-bold text-text-primary tracking-tight">Lead Explorer</h1>
        <p className="text-text-secondary font-medium mt-1">Search and filter your connected lead databases.</p>
      </div>

      <div className="bg-bg-card rounded-card shadow-card border border-border-light p-6">
        <form onSubmit={(e) => { e.preventDefault(); handleSearch(1); }} className="space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div className="flex flex-col sm:flex-row items-center gap-4 flex-1">
              <div className="w-full lg:max-w-[240px] space-y-1.5">
                <label className="text-[11px] font-bold text-text-muted uppercase tracking-wider ml-1">Website (Buyer)</label>
                <select
                  name="buyerId"
                  className="block w-full px-3.5 py-2.5 border border-border-light rounded-input bg-bg-soft focus:ring-2 focus:ring-primary-light focus:border-primary transition-all outline-none text-[14px] font-medium text-text-primary shadow-small"
                  value={stagedFilters.buyerId}
                  onChange={handleBuyerChange}
                  required
                >
                  <option value="" disabled>Select Buyer...</option>
                  {buyers.map(b => <option key={b.buyerID} value={b.buyerID}>{b.companyName}</option>)}
                </select>
              </div>

              <div className="w-full lg:max-w-[240px] space-y-1.5">
                <label className="text-[11px] font-bold text-text-muted uppercase tracking-wider ml-1">Supplier Code</label>
                <select
                  name="supplierCode"
                  className="block w-full px-3.5 py-2.5 border border-border-light rounded-input bg-bg-soft focus:ring-2 focus:ring-primary-light focus:border-primary transition-all outline-none text-[14px] font-medium disabled:opacity-50 text-text-primary shadow-small"
                  value={stagedFilters.supplierCode}
                  onChange={handleFilterChange}
                  disabled={!stagedFilters.buyerId || suppliers.length === 0}
                  required
                >
                  <option value="" disabled>{stagedFilters.buyerId ? "Select Code..." : "Choose Website First..."}</option>
                  {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <motion.button
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isLoading || !stagedFilters.buyerId || !stagedFilters.supplierCode}
                animate={{ backgroundColor: isSuccess ? "var(--color-status-complete)" : "var(--color-primary)" }}
                className="inline-flex items-center justify-center px-6 py-2.5 bg-primary text-white font-bold rounded-button shadow-card transition-all disabled:opacity-70 min-w-[140px]"
              >
                {isLoading ? <><Loader2 className="animate-spin h-4 w-4 mr-2" />Searching</> : isSuccess ? <Check className="w-5 h-5" /> : <><Search className="w-4 h-4 mr-2" />Run Search</>}
              </motion.button>

              <div className="h-8 w-[1px] bg-border-light mx-1 hidden sm:block"></div>

              <motion.button
                whileTap={{ scale: 0.95 }}
                type="button"
                onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                className={`p-2.5 rounded-button border transition-all shadow-small ${isFiltersOpen ? 'bg-primary-light border-primary/30 text-primary shadow-inner' : 'bg-bg-card border-border-medium text-text-secondary hover:bg-bg-soft'}`}
                title="Advanced Filters"
              >
                <SlidersHorizontal className="w-4 h-4" />
              </motion.button>

              <motion.button
                whileTap={{ scale: 0.95 }}
                type="button"
                onClick={handleExportCSV}
                disabled={leads.length === 0}
                className="p-2.5 rounded-button border border-border-medium bg-bg-card text-text-secondary hover:bg-bg-soft disabled:opacity-50 transition-all shadow-small"
                title="Export CSV"
              >
                <Download className="w-4 h-4" />
              </motion.button>

              <motion.button
                whileTap={{ scale: 0.95 }}
                type="button"
                onClick={handleClearFilters}
                className="p-2.5 rounded-button border border-border-medium bg-bg-card text-text-secondary hover:text-[#E53E3E] hover:bg-[#FFF5F5] transition-all shadow-small"
                title="Reset All"
              >
                <X className="w-4 h-4" />
              </motion.button>
            </div>
          </div>

          <AnimatePresence>
            {isFiltersOpen && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-6 border-t border-border-light">
                  <div className="lg:col-span-4 mb-1">
                    <h3 className="text-[11px] font-bold text-text-muted uppercase tracking-wider ml-1">Advanced Search Parameters</h3>
                  </div>
                  {["fromDate", "toDate", "search", "status", "phone", "minPrice", "maxPrice"].map((f) => (
                    <div key={f} className="space-y-1.5 relative">
                      <label className="text-[12px] font-medium text-text-secondary ml-1">{f.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</label>
                      <input
                        type={f.includes('Date') ? "date" : f.includes('Price') ? "number" : "text"}
                        name={f}
                        placeholder={f === 'search' ? "Name, Email..." : f === 'status' ? "Sold, Rejected" : "Enter value..."}
                        className="block w-full px-[12px] py-[10px] border border-border-light rounded-input bg-bg-soft focus:ring-2 focus:ring-primary-light focus:border-primary transition-all outline-none text-[14px] text-text-primary shadow-small"
                        value={stagedFilters[f]}
                        onChange={handleFilterChange}
                      />
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </div>

      <AnimatePresence>
        {activeFilterChips.length > 0 && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="flex flex-wrap items-center gap-2 px-1">
            <span className="text-[11px] font-bold text-text-muted uppercase tracking-wider mr-1">Applied:</span>
            {activeFilterChips.map((chip) => (
              <motion.div key={chip.key} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }} layout className="flex items-center gap-1.5 bg-primary-light/40 border border-primary/10 text-primary px-3 py-1 rounded-pill shadow-small">
                <span className="text-[11px] font-bold opacity-60">{chip.label}:</span>
                <span className="text-[12px] font-bold">{chip.value}</span>
                <button onClick={() => removeFilter(chip.key)} className="ml-1 hover:text-primary-hover transition-colors"><X className="w-3 h-3" /></button>
              </motion.div>
            ))}
            <button onClick={handleClearFilters} className="text-[11px] font-bold text-text-muted hover:text-text-primary transition-all underline underline-offset-4 decoration-border-medium px-2">Clear all</button>
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="p-4 bg-bg-warning border border-status-pending rounded-card flex items-start gap-3 shadow-small">
          <AlertCircle className="w-5 h-5 text-feedback-warning flex-shrink-0" />
          <p className="text-[14px] text-text-primary font-medium">{error}</p>
        </motion.div>
      )}

      <div className="bg-bg-card shadow-card rounded-card border border-border-light overflow-hidden">
        <div className="overflow-x-auto min-h-[400px]">
          <table className="min-w-full divide-y divide-border-light">
            <thead className="bg-bg-soft/50">
              <tr>
                {tableHeaders.length > 0 ? tableHeaders.map((header) => {
                  const isActive = isHeaderActive(header);
                  return (
                    <th
                      key={header}
                      scope="col"
                      className={`px-6 py-4 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap transition-colors duration-500 ${isActive ? 'bg-primary-light/30 text-primary' : 'text-text-muted'}`}
                    >
                      {header.replace(/_/g, ' ')}
                      {isActive && <span className="ml-1.5 text-[8px] px-1 py-0.5 rounded bg-primary text-white">Filtered</span>}
                    </th>
                  );
                }) : <th className="px-6 py-4 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider">Results</th>}
              </tr>
            </thead>
            <AnimatePresence mode="wait">
              <motion.tbody
                key={currentPage + "_" + leads.length + (leads.length > 0 ? '_data' : '_empty')}
                variants={tableVariants}
                initial="hidden"
                animate="visible"
                className="bg-bg-card divide-y divide-border-light"
              >
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={`skel-${i}`}>
                      {Array.from({ length: Math.max(tableHeaders.length, 5) }).map((_, j) => (
                        <td key={`skel-c-${j}`} className="px-6 py-4"><div className="h-4 bg-bg-soft rounded-[4px] animate-pulse w-3/4"></div></td>
                      ))}
                    </tr>
                  ))
                ) : leads.length > 0 ? (
                  leads.map((lead, idx) => (
                    <motion.tr key={`row-${currentPage}-${idx}`} variants={rowVariants} className="hover:bg-bg-soft/40 transition-colors">
                      {tableHeaders.map((header) => {
                        const isActive = isHeaderActive(header);
                        return (
                          <td
                            key={`${idx}-${header}`}
                            className={`px-6 py-3 whitespace-nowrap text-[13px] font-medium transition-colors duration-500 ${isActive ? 'bg-primary-light/15 text-primary' : 'text-text-primary'}`}
                          >
                            {header.toLowerCase().includes('status') ? (
                              <span className={`inline-flex items-center px-[6px] py-[2px] rounded-[6px] text-[11px] font-bold ${String(lead[header]).toLowerCase().includes('sold') || String(lead[header]).toLowerCase().includes('accepted') ? 'bg-bg-success text-feedback-success' : 'bg-bg-soft text-text-secondary'
                                }`}>{String(lead[header] || "N/A")}</span>
                            ) : String(lead[header] || "N/A")}
                          </td>
                        );
                      })}
                    </motion.tr>
                  ))
                ) : (
                  <motion.tr variants={rowVariants}>
                    <td colSpan={Math.max(tableHeaders.length, 1)} className="px-6 py-24 text-center">
                      <div className="flex flex-col items-center justify-center text-text-muted">
                        <Database className="w-12 h-12 mb-4 opacity-20" />
                        <h3 className="text-[18px] font-bold text-text-primary tracking-tight">{hasSearched ? "No leads found" : "Ready to search"}</h3>
                        <p className="text-[14px] text-text-secondary font-medium mt-1 max-w-sm">{hasSearched ? "Try adjusting your filters." : "Select a Website and Supplier to begin."}</p>
                      </div>
                    </td>
                  </motion.tr>
                )}
              </motion.tbody>
            </AnimatePresence>
          </table>
        </div>

        {leads.length > 0 && !isLoading && (
          <div className="bg-bg-card px-6 py-4 border-t border-border-light flex items-center justify-between">
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <p className="text-[14px] text-text-secondary font-medium">Page <span className="font-bold text-text-primary">{currentPage}</span></p>
              <nav className="relative z-0 inline-flex rounded-button shadow-small -space-x-px">
                <motion.button whileTap={{ scale: 0.95 }} onClick={() => handleSearch(currentPage - 1)} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-button border border-border-light bg-bg-card text-[14px] font-medium text-text-secondary hover:bg-bg-soft disabled:opacity-50 transition-colors focus:z-10 focus:ring-2 focus:ring-primary-light"><ChevronLeft className="h-5 w-5" /></motion.button>
                <span className="relative inline-flex items-center px-4 py-2 border border-border-light bg-bg-soft text-[14px] font-bold text-text-primary">{currentPage}</span>
                <motion.button whileTap={{ scale: 0.95 }} onClick={() => handleSearch(currentPage + 1)} disabled={leads.length < pageSize} className="relative inline-flex items-center px-2 py-2 rounded-r-button border border-border-light bg-bg-card text-[14px] font-medium text-text-secondary hover:bg-bg-soft disabled:opacity-50 transition-colors focus:z-10 focus:ring-2 focus:ring-primary-light"><ChevronRight className="h-5 w-5" /></motion.button>
              </nav>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
